import { unwrap } from "idb";
import type { HasTab, RecordableEvent } from "../../lib/Events";
import { EventUpdatedInDatabase, NewEventsAddedToDatabase } from "../StateEvents";
import {
	type EventReadyToWriteToDb, type DbRecordedEvent,
	type EventMetadataParameter,
	nowAsISODateTimeString,
	UUID
} from "./DatabaseTypes";
import { DbEventsTableReader } from "./DbEventsTableReader";
import { EvidenceDatabase } from "./EvidenceDbStruct";
import { recordableEventToEventReadyToWriteToDb } from "./recordableEventToDbReadyToRecordEvent";
import { ProcessInfo } from "../ProcessInfo";
import { executeEventually } from "../../lib/utils/executeEventually";

export class DbEventsTableCache {
	
}

export class DbEventsTableWriter extends DbEventsTableReader {
	constructor(db: EvidenceDatabase, recordingUuid: UUID) {
		super(db, recordingUuid);
		
	}

	eventIndex: number = 0;

	readonly maxWriteBlockSize = 1000;
	readonly minTimeBetweenWritesMs = 200;
	readonly maxLatencyMs = 200;
	writeQueue: DbRecordedEvent[] = [];

	writerRunning: boolean = false;
	emptyEventWriteQueue = executeEventually(async () => {
		this.writerRunning = true;
		const {db} = this;
		while (this.writeQueue.length > 0) {
			let writtenButNotCommitted: DbRecordedEvent[] = [];
			const startedAt = performance.now();
			const tx = db.transaction("events", "readwrite");
			const transactionCompletionPromise = new Promise<void>( (resolve, reject) => {
				tx.oncomplete = () => resolve();
				tx.onerror= (e) => reject(e);
			});
			const os = unwrap(tx.objectStore("events"));
			for (let event = this.writeQueue.shift(); event != null; event = this.writeQueue.shift()) {
				os.add(event);
				writtenButNotCommitted.push(event);
				if (writtenButNotCommitted.length >= this.maxWriteBlockSize) {
					break;
				}
				const latency = performance.now() - startedAt;
				if (latency > this.maxLatencyMs) {
					break;
				}
			}
			tx.commit();
			try {
				await transactionCompletionPromise;
				const latency = performance.now() - startedAt;
				const msToWaitBeforeNextWrite = Math.max(this.minTimeBetweenWritesMs, latency);
				NewEventsAddedToDatabase.send({
					recordingUuid: this.recordingUuid,
					eventsInfo: writtenButNotCommitted.map( ({eventIndex, category, eventName}) => ({
						eventIndex, category, eventName
					}))
				});
				if (latency > 2 * this.maxLatencyMs) {
					ProcessInfo.log(`writeFromQueue: latency: ${Math.floor(latency)}ms for ${writtenButNotCommitted.length} of ${this.writeQueue.length + writtenButNotCommitted.length} events, waiting ${msToWaitBeforeNextWrite}ms`);
				}
				writtenButNotCommitted = [];
				await new Promise( (resolve) => setTimeout(resolve, msToWaitBeforeNextWrite));
			} catch {
				const latency = performance.now() - startedAt;
				const msToWaitBeforeNextWrite = Math.max(this.minTimeBetweenWritesMs, latency);
				ProcessInfo.errorLog(`writeFromQueue: FAILED latency: ${Math.floor(latency)} ms for ${writtenButNotCommitted.length} of ${this.writeQueue.length + writtenButNotCommitted.length} events, waiting ${msToWaitBeforeNextWrite}ms`);
				this.writeQueue.unshift(...writtenButNotCommitted);
				await new Promise( (resolve) => setTimeout(resolve, msToWaitBeforeNextWrite));
				}
			//if (latency > 1.5 * this.maxLatencyMs) {
			//	ProcessInfo.log(`writeFromQueue: latency: ${latency} ms for ${writtenButNotCommitted.length} events`);
			//}
			// Wait for at least as long as the write took
		}
		this.writerRunning = false;
	});

	addEventToWriteQueue = (event: DbRecordedEvent) => {
		this.writeQueue.push(event);
		if (!this.writerRunning) {
			this.emptyEventWriteQueue();
		}
	}



	addDbEvent = /* async */ <
		EVENT extends RecordableEvent
	>(
		readyToRecordEvent: EventReadyToWriteToDb<EVENT>
//	): Promise<DbRecordedEvent<EVENT>> => {
	): DbRecordedEvent<EVENT> => {
		// const startTime = performance.now()
		const {recordingUuid} = this;
		const eventIndex = this.eventIndex++;
		const whenRecordedIso = nowAsISODateTimeString();
		const dbRecordedEvent: DbRecordedEvent<EVENT> = {
			...readyToRecordEvent,
			eventIndex,
			whenRecordedIso,
			recordingUuid,
		} satisfies DbRecordedEvent<EVENT>;
		// This was slow...
		// db.add("events", dbRecordedEvent);
		// Do the insert without any callbacks to maximize performance.
		this.addEventToWriteQueue(dbRecordedEvent);
		// const tx = this.idbDatabase.transaction("events", "readwrite", {durability: "relaxed"});
		// // Remove all handlers to maximize performance'
		// tx.oncomplete = null;
		// tx.onerror = null;
		// tx.onabort = null;
		// const os = tx.objectStore("events");
		// os.add(dbRecordedEvent);
		// tx.commit();
		// Let the rest of the app know that a new event has been added to the database
		// const latencyMS = performance.now() - startTime;
		return { ...dbRecordedEvent, eventIndex } as DbRecordedEvent<EVENT>;
	}

	updateEvent = async<EVENT extends RecordableEvent>(
		event: DbRecordedEvent<EVENT>
	) => {
		const {db} = this;
		const updatedEventWithWhenUpdatedTimestamp = {...event, whenUpdatedIso: nowAsISODateTimeString()};
		await db.put("events", updatedEventWithWhenUpdatedTimestamp);
		const {category, eventName, eventIndex, recordingUuid} = updatedEventWithWhenUpdatedTimestamp;
		EventUpdatedInDatabase.send({recordingUuid, eventsInfo: [{category, eventName, eventIndex}]});
	}

	static #forRecordingUuid = new Map<UUID, DbEventsTableWriter>();
	// static override forRecordingUuid = async (recordingUuid: UUID) => {
	// 	const cached = DbEventsTableWriter.#forRecordingUuid.get(recordingUuid);
	// 	if (cached != null) return cached;
	// 	const db = await requireEvidenceDatabase();
	// 	return mapGetOrCreate(DbEventsTableWriter.#forRecordingUuid, recordingUuid, () => new DbEventsTableWriter(db, recordingUuid));
	// };
	static override forDbAndRecordingUuid = (db: EvidenceDatabase, recordingUuid: UUID) => DbEventsTableWriter.#forRecordingUuid.get(recordingUuid) ?? ( () => {
		const evidenceDbForRecording = new DbEventsTableWriter(db, recordingUuid);
		DbEventsTableWriter.#forRecordingUuid.set(recordingUuid, evidenceDbForRecording);
		return evidenceDbForRecording;
	})();
	addRecordableEvent = <
		EVENT extends RecordableEvent
	>(
		event: EVENT & HasTab,
		options: EventMetadataParameter = {}
	) => {
		const eventReadyToWriteToDb: EventReadyToWriteToDb<EVENT> =
		recordableEventToEventReadyToWriteToDb<EVENT>(this.recordingUuid, event, options);
		return this.addDbEvent<EVENT>(eventReadyToWriteToDb);
	}

	static addRecordableEvent = async <
		EVENT extends RecordableEvent
	>(
		db: EvidenceDatabase,
		recordingUuid: UUID,
		recordableEvent: EventReadyToWriteToDb<EVENT>,
		options: EventMetadataParameter = {}
	) => DbEventsTableWriter.forDbAndRecordingUuid(db, recordingUuid)
			.addRecordableEvent<EVENT>(recordableEvent, options);
	
	static getEventsByEventCategoryEventNameAndRequestId = async <	
			EVENT extends RecordableEvent,
		>(
			eventCategory: EVENT["category"],
			eventName: EVENT["eventName"],
			recordingUuid: UUID,
			requestId: string
		) => await (await DbEventsTableWriter.forRecordingUuid(recordingUuid)).getEventsByCategoryThenNameThenRequest(eventCategory, eventName)(requestId);
	
		

	static override removeRecordingUuid = (recordingUuid: UUID) => DbEventsTableWriter.#forRecordingUuid.delete(recordingUuid);

}
